import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import StickyButton from '@/components/atom/buttons/StickyButton';
import BottomSheet from '@/components/molecules/BottomSheet';
import { SelectedDiagnosisSlot } from '@/components/templates/doctor/detail/useDoctorDetailViewModel';
import { BookingScheduleStatus, BookingScheduleTypeVo, NonFaceToFaceDiagnosisBookingSchedulesVo } from '@/types/doctor';
import DiagnosisEmptySlot from './DiagnosisEmptySlot';

import { Button } from 'doctornow-design-system';
interface Props {
  isOpened: boolean;
  bookingSchedules: NonFaceToFaceDiagnosisBookingSchedulesVo[];
  selectedDiagnosisSlot?: SelectedDiagnosisSlot;
  onClose: () => void;
  onChangeDiagnosisSlot: (selectedDiagnosisSlot: SelectedDiagnosisSlot) => void;
  requestTreatmentWithSlot: () => void;
}

const TITLE_HEIGHT = 30;
const DAYS_WRAPPER_HEIGHT = 86;
const TIME_SLOT_WRAPPER_MARGIN_BOTTOM = 40;
const TIME_SLOT_TOP_GRADIENT_SCROLL_TOP = 24;

const DiagnosisSlotBottomSheet = ({
  isOpened,
  bookingSchedules,
  selectedDiagnosisSlot,
  onClose,
  onChangeDiagnosisSlot,
  requestTreatmentWithSlot,
}: Props) => {
  const timeSlotWrapperRef = useRef<HTMLDivElement>(null);
  const [isTimeSlotTopGradient, setIsTimeSlotTopGradient] = useState(false);
  const [isTimeSlotBottomGradient, setIsTimeSlotBottomGradient] = useState(true);

  const selectedSlot =
    bookingSchedules.find((schedule) => schedule.targetDate === selectedDiagnosisSlot?.targetDate) ??
    bookingSchedules[0];

  const setTimeSlotTopGradient = useCallback(() => {
    if (timeSlotWrapperRef?.current) {
      if (timeSlotWrapperRef.current?.scrollTop > TIME_SLOT_TOP_GRADIENT_SCROLL_TOP) {
        return setIsTimeSlotTopGradient(true);
      }
      return setIsTimeSlotTopGradient(false);
    }
  }, []);

  const setTimeSlotBottomGradient = useCallback(() => {
    if (timeSlotWrapperRef?.current) {
      if (
        timeSlotWrapperRef.current.scrollTop + timeSlotWrapperRef.current.offsetHeight !==
        timeSlotWrapperRef.current.scrollHeight
      ) {
        return setIsTimeSlotBottomGradient(true);
      }
      return setIsTimeSlotBottomGradient(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpened) return;
    if (!timeSlotWrapperRef.current) return;
    const ref = timeSlotWrapperRef.current;
    ref.addEventListener('scroll', () => {
      setTimeSlotTopGradient();
      setTimeSlotBottomGradient();
    });
    return () => {
      if (!timeSlotWrapperRef) return;
      ref.removeEventListener('scroll', () => {
        setTimeSlotTopGradient();
        setTimeSlotBottomGradient();
      });
    };
  }, [isOpened, setTimeSlotTopGradient, setTimeSlotBottomGradient]);

  return (
    <BottomSheet index={10} isOpened={isOpened} onClickOverlay={onClose}>
      <Wrapper isBookable={selectedSlot.status === BookingScheduleStatus.Bookable}>
        <Contents>
          <Title>언제 진료받으시나요?</Title>
          <DayWrapper>
            {bookingSchedules.map((bookingSchedule) => (
              <DaySelect
                key={bookingSchedule.targetDate}
                selected={selectedSlot.targetDate === bookingSchedule.targetDate}
                onClick={() => {
                  onChangeDiagnosisSlot({
                    targetDate: bookingSchedule.targetDate,
                    bookingStatus: bookingSchedule.status as unknown as BookingScheduleTypeVo,
                  });
                }}
              >
                {bookingSchedule.displayName}
              </DaySelect>
            ))}
          </DayWrapper>

          {selectedSlot.status !== BookingScheduleStatus.Bookable ? (
            <DiagnosisEmptySlot bookingScheduleStatus={selectedSlot.status} />
          ) : (
            <TimeSlotContainer>
              {isTimeSlotTopGradient && <Gradient top />}
              <TimeSlotWrapper ref={timeSlotWrapperRef}>
                {selectedSlot.schedules.map((schedule) => (
                  <TimeSlot
                    key={schedule.startTimeSlot.value}
                    isSelected={selectedDiagnosisSlot?.startTimeSlotValue === schedule.startTimeSlot.value}
                    disabled={schedule.bookingStatus !== BookingScheduleTypeVo.Bookable}
                    onClick={() =>
                      onChangeDiagnosisSlot({
                        targetDate: selectedSlot.targetDate,
                        startTimeSlotValue: schedule.startTimeSlot.value,
                        endTimeSlotValue: schedule.endTimeSlot.value,
                        bookingStatus: schedule.bookingStatus,
                      })
                    }
                  >
                    {schedule.startTimeSlot.fromDisplayTime}
                    <span>~{schedule.endTimeSlot.toDisplayTime}</span>
                  </TimeSlot>
                ))}
              </TimeSlotWrapper>
              {isTimeSlotBottomGradient && <Gradient bottom />}
            </TimeSlotContainer>
          )}
        </Contents>
      </Wrapper>
      <StickyButton>
        <Button
          size={'xl'}
          className={'w-full'}
          disabled={selectedDiagnosisSlot?.startTimeSlotValue === undefined}
          onClick={requestTreatmentWithSlot}
        >
          진료 신청서 작성하기
        </Button>
      </StickyButton>
    </BottomSheet>
  );
};

export default DiagnosisSlotBottomSheet;

const Wrapper = styled.div<{ isBookable: boolean }>`
  padding: 12px 20px 0;
  ${({ theme, isBookable }) => `
    height: calc(100vh * ${isBookable ? 0.8 : 0.5} - ${theme.size.stickyButton.height}px);
  `}
`;

const Contents = styled.div`
  margin: 0 4px;
`;

const Title = styled.div`
  height: ${TITLE_HEIGHT}px;

  ${({ theme }) => `
    ${theme.typography.UI22SB};
    color: ${theme.color.gray900};
  `};
`;

const DayWrapper = styled.div`
  display: flex;
  gap: 8px;
  padding: 20px 0 24px 0;
  height: ${DAYS_WRAPPER_HEIGHT}px;
`;

const DaySelect = styled.div<{ selected: boolean }>`
  width: 100%;
  text-align: center;

  ${({ theme, selected }) => `
    ${theme.typography.UI14SB};
    border-radius: 8px;
    padding: 10px 20px;
    color: ${selected ? theme.color.primary700 : theme.color.gray600};
    border: 1px solid ${selected ? theme.color.primary600 : theme.color.gray100};
  `}
`;

const TimeSlotContainer = styled.div`
  position: relative;
`;

const TimeSlotWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 3fr));
  gap: 8px;
  overflow: scroll;
  align-content: flex-start;

  ${({ theme }) => `
    height: calc(100vh * 0.8 - ${
      TITLE_HEIGHT + DAYS_WRAPPER_HEIGHT + TIME_SLOT_WRAPPER_MARGIN_BOTTOM + theme.size.stickyButton.height
    }px);
  `}
`;

const TimeSlot = styled.button<{ isSelected: boolean }>`
  padding: 7px 28px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 48px;

  ${({ theme, isSelected }) => `
    ${theme.typography.UI14SB};
    color: ${isSelected ? theme.color.primary700 : theme.color.gray600};
    border: 1px solid ${isSelected ? theme.color.primary600 : theme.color.gray100};

    span {
      ${theme.typography.UI12M};
      color: ${theme.color.gray300};
      margin-top: -4px;
    }
    
    &:disabled {
      ${theme.typography.UI14M};
      color: ${theme.color.gray300};
      border: 1px solid ${theme.color.gray40};
      background: ${theme.color.gray40};

      span {
        color: ${theme.color.gray300};
      }
    }
  `}
`;

const Gradient = styled.div<{ top?: boolean; bottom?: boolean }>`
  position: absolute;
  height: 20px;
  width: 100%;
  right: 0;
  ${({ top, bottom }) => `
    ${
      top &&
      `
        top: -1px;
        background: linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%); 
      `
    };
    ${
      bottom &&
      `
        bottom: -1px;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 100%);
      `
    }
  `}
`;
